import React from 'react';
import FontFace from './FontFace';
import type { FontFamily, FontFamilyGroup } from './PageContext';
import type { Font } from './FontFace';
import { createStaticAssetUrl } from '../utils/urlHelper';
import notUndefined from '../utils/notUndefined';

type UnionFontFamily = FontFamilyGroup['fontFamilies'][number] | FontFamily;
type UnionFontStyle = UnionFontFamily['fontStyles'][number];

function notDuplicateFont(font: Font, index: number, fonts: Font[]): boolean {
    const matchIndex = fonts.findIndex(
        (searchFont): boolean =>
            searchFont.fontStyle === font.fontStyle &&
            searchFont.fontFamily === font.fontFamily &&
            searchFont.fontWeight === font.fontWeight &&
            searchFont.woff2Url === font.woff2Url,
    );
    return matchIndex === index;
}

function GlobalRemoteFonts({
    fontFamily,
    fontFamilyGroup,
}: {
    fontFamily?: FontFamily;
    fontFamilyGroup?: FontFamilyGroup;
}): React.ReactElement | null {
    if (!fontFamily && !fontFamilyGroup) {
        return null;
    }
    const fontFamilies: UnionFontFamily[] = fontFamilyGroup?.fontFamilies
        ? fontFamilyGroup.fontFamilies
        : [];
    if (fontFamily) {
        fontFamilies.push(fontFamily);
    }

    const remoteFonts: Font[] = fontFamilies
        .reduce(
            (carry: UnionFontStyle[], fontFamily): UnionFontStyle[] =>
                carry.concat(fontFamily.fontStyles),
            [],
        )
        .map((fontStyle): Font | undefined => {
            if (!fontStyle.cssRenderInfo) {
                return;
            }
            return {
                fontFamily: fontStyle.cssRenderInfo.fontFamilyName,
                fontWeight: fontStyle.cssRenderInfo.fontWeight || 'normal',
                fontStyle: fontStyle.cssRenderInfo.isItalic
                    ? 'italic'
                    : 'normal',
                woff2Url: createStaticAssetUrl(
                    fontStyle.cssRenderInfo.woff2Path,
                ),
            };
        })
        .filter(notUndefined)
        .filter(notDuplicateFont);

    if (!remoteFonts.length) {
        return null;
    }

    return <FontFace fonts={remoteFonts} preventFallbackFont />;
}

export default React.memo(GlobalRemoteFonts);
